import { PreviewProps } from '@susu/custom-made/components/preview/preview';
import { Fabric, HiddenOptionsAndValues, LayerDefinitions, OptionsSelection, typeOptionValue } from '../interfaces';
import { createLayerUrlFromTemplate, createPreviewData } from '../imageUrlProvider/imageUrlProvider';
import {
  ConfigurationTypes,
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
  Products,
  ProductType,
} from '../productDefinition';
import { isExtraConfigurationInProduct, isObjectEmpty } from '../utils';

export interface LayerDefinition {
  src: string;
  srcset: string;
  srcSheet: string;
  srcRotation: string;
  srcsetRotation: string;
  sizes: string;
}

export const getProductPreviewLayers = async (
  layerDefinitionsPerConfiguration: LayerDefinitions,
  optionValues: typeOptionValue,
  selectedFabric: Fabric,
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  product: ProductType,
  filterNonExistingLayers: boolean = false,
) => {
  let imgTemplates = new Map<string, PreviewProps>();
  if (!layerDefinitionsPerConfiguration || !optionValues || !selectedFabric || !selectedOptions) return imgTemplates;

  const tmpSelectedOptions: OptionsSelection = structuredClone(selectedOptions);

  for (const [configurationTypeId, configurationOptionSelection] of Object.entries(tmpSelectedOptions)) {
    Object.entries(configurationOptionSelection).forEach(([option, optionValue]) => {
      if (
        hiddenOptionsAndValues.optionsPerConfiguration[+configurationTypeId]?.includes(+option) ||
        hiddenOptionsAndValues.optionValuesPerConfiguration[+configurationTypeId]?.includes(optionValue)
      ) {
        delete tmpSelectedOptions[+configurationTypeId][+option];
      }
    });
  }

  for (const configurationTypeId of Products[product]) {
    if (isExtraConfigurationInProduct(product, configurationTypeId)) continue;

    const { fallbackUrl, layerDefinitions } = layerDefinitionsPerConfiguration.configurationTypes[+configurationTypeId];
    const processedImageTemplates = layerDefinitions.map(def => {
      return createLayerUrlFromTemplate(
        def.imageTemplate,
        optionValues,
        tmpSelectedOptions,
        selectedFabric,
        configurationTypeId,
      );
    });

    const previewData = await createPreviewData(processedImageTemplates, fallbackUrl, filterNonExistingLayers);

    imgTemplates.set(configurationTypeId, previewData);
  }

  return imgTemplates;
};

export const getExtraProductPreviewLayers = async (
  layerDefinitionsPerConfiguration: LayerDefinitions,
  optionValues: typeOptionValue,
  selectedFabric: Fabric,
  selectedOptions: OptionsSelection,
  hiddenOptionsAndValues: HiddenOptionsAndValues,
  extraProductConfigurationId: ConfigurationTypes,
) => {
  let imgTemplates = new Map<string, PreviewProps>();
  if (!layerDefinitionsPerConfiguration || !optionValues || !selectedFabric || !selectedOptions) return imgTemplates;

  const tmpSelectedOptions: OptionsSelection = structuredClone(selectedOptions);

  if (
    !tmpSelectedOptions[+extraProductConfigurationId] ||
    isObjectEmpty(tmpSelectedOptions[+extraProductConfigurationId])
  )
    return imgTemplates;

  Object.entries(tmpSelectedOptions[+extraProductConfigurationId]).forEach(([option, optionValue]) => {
    if (
      hiddenOptionsAndValues.optionsPerConfiguration[+extraProductConfigurationId]?.includes(+option) ||
      hiddenOptionsAndValues.optionValuesPerConfiguration[+extraProductConfigurationId]?.includes(optionValue)
    ) {
      delete tmpSelectedOptions[+extraProductConfigurationId][+option];
    }
  });
  // To handle extra configurations which have different configuration type id but using definitions from base product
  const baseConfigurationTypeId =
    getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+extraProductConfigurationId);

  const { fallbackUrl, layerDefinitions } =
    layerDefinitionsPerConfiguration.configurationTypes[baseConfigurationTypeId];
  const processedImageTemplates = layerDefinitions.map(def => {
    return createLayerUrlFromTemplate(
      def.imageTemplate,
      optionValues,
      tmpSelectedOptions,
      selectedFabric,
      extraProductConfigurationId.toString() as ConfigurationTypes,
    );
  });

  const previewData = await createPreviewData(processedImageTemplates, fallbackUrl, false);
  imgTemplates.set(extraProductConfigurationId, previewData);

  return imgTemplates;
};
